import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authService } from '../services/authService';
import { pushNotificationService } from '../services/pushNotificationService';

// Authentication context for user session management
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  isSeller: () => boolean;
  isPicker: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateAndLoadUser = async () => {
      const currentUser = authService.getCurrentUser();
      
      if (!currentUser) {
        setLoading(false);
        return;
      }

      // Validate token by making a test request
      try {
        await authService.validateToken();
        setUser(currentUser);
      } catch (error: any) {
        console.error('Token validation failed:', error);
        console.error('Error response:', error?.response);
        console.error('Error status:', error?.response?.status);
        console.error('Error data:', error?.response?.data);
        
        // Only logout if it's a 401 (unauthorized) or token error
        // 403 might be a permission issue, not invalid token
        if (error?.response?.status === 401 || error?.message?.includes('token')) {
          console.log('Invalid or expired token, logging out');
          authService.logout();
          setUser(null);
        } else if (error?.response?.status === 403) {
          // Log detailed 403 error but keep user logged in
          console.warn('403 Forbidden error during token validation. Error details:', {
            url: error?.config?.url,
            method: error?.config?.method,
            headers: error?.config?.headers,
            data: error?.response?.data
          });
          setUser(currentUser);
        } else {
          // For other errors, keep user logged in but log warning
          console.warn('Token validation returned error but keeping user logged in:', error?.response?.status);
          setUser(currentUser);
        }
      } finally {
        setLoading(false);
      }
    };

    validateAndLoadUser();
  }, []);

  const login = async (username: string, password: string) => {
    const response = await authService.login({ username, password });
    setUser(response.user);
    
    // Request push notification permission after successful login
    // Do this in background without blocking login flow
    setTimeout(() => {
      console.log('[AuthContext] Checking push notification support...');
      console.log('[AuthContext] Is supported:', pushNotificationService.isSupported());
      console.log('[AuthContext] Current permission:', Notification.permission);
      
      if (pushNotificationService.isSupported()) {
        console.log('[AuthContext] Requesting push notification permission...');
        pushNotificationService.requestPermission()
          .then(granted => {
            console.log('[AuthContext] Permission request result:', granted);
          })
          .catch(err => {
            console.warn('[AuthContext] Push notification setup failed:', err);
          });
      } else {
        console.warn('[AuthContext] Push notifications not supported');
      }
    }, 2000); // Wait 2 seconds after login to avoid overwhelming user
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const isAdmin = () => {
    return user?.role?.name === 'Admin' || user?.role?.name === 'SuperAdmin';
  };

  const isSuperAdmin = () => {
    return user?.role?.name === 'SuperAdmin';
  };

  const isSeller = () => {
    return user?.role?.name === 'Seller';
  };

  const isPicker = () => {
    return user?.role?.name === 'Picker';
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="text-gray-600">Loading...</div>
    </div>;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
        isAdmin,
        isSuperAdmin,
        isSeller,
        isPicker,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
